import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Lock,
  LogOut,
  Search,
  Download,
  Loader2,
  Mail,
  Users,
  Trophy,
  FileSignature,
  FileDown,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  KeyRound,
  FileText,
  Trash2,
  Target,
  HeartHandshake,
} from "lucide-react";
import { Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ao_admin_token";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-carddark px-4 py-3.5 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-300 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon";

const errText = (detail) => {
  if (!detail) return "Ceva nu a mers bine. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || "").join(" ");
  return String(detail);
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" });
};

const LoginCard = ({ onLogin }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { code });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
      toast.success("Bine ai venit!");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70svh] max-w-md items-center px-4">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full rounded-3xl border border-white/10 bg-surface p-8 sm:p-10"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-carddark text-neon">
          <Lock className="h-5 w-5" />
        </span>
        <h1 className="mt-6 font-display text-2xl font-black uppercase tracking-tight text-white">
          Panou Staff
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-neutral-400">
          Introdu codul de acces pentru a vedea mesajele și înscrierile.
        </p>
        <div className="mt-8">
          <label
            htmlFor="admin-code"
            className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400"
          >
            Cod de acces
          </label>
          <input
            id="admin-code"
            data-testid="admin-input-code"
            type="password"
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="••••••••"
            className={`${inputCls} text-center tracking-[0.3em]`}
          />
        </div>
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Intră în panou
        </button>
        <p className="mt-6 text-xs leading-relaxed text-neutral-500">
          Toate mesajele, înscrierile și scanurile ajung automat și pe email, la
          contact@artandoutdoor.com.
        </p>
      </motion.form>
    </div>
  );
};

const TABS = [
  { id: "messages", label: "Mesaje Contact", icon: Mail, testid: "admin-tab-messages" },
  { id: "waitlist", label: "Listă RISE 27'", icon: Users, testid: "admin-tab-waitlist" },
  { id: "cupa", label: "Cupa · Interes", icon: Trophy, testid: "admin-tab-cupa" },
  {
    id: "cupa/registrations",
    label: "Cupa · Înscrieri semnate",
    icon: FileSignature,
    testid: "admin-tab-cupa-registrations",
  },
  { id: "form230", label: "Formulare 230", icon: FileText, testid: "admin-tab-form230" },
  { id: "sponsorships", label: "Sponsorizări", icon: HeartHandshake, testid: "admin-tab-sponsorships" },
];

const downloadPdf = async (url, filename, token, successMsg = "PDF descărcat") => {
  try {
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const objectUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
    toast.success(successMsg);
  } catch (err) {
    toast.error("Descărcarea PDF a eșuat.");
  }
};

const SPONSOR_STATUS = {
  pending: { label: "În așteptare", cls: "text-neutral-300 border-white/20" },
  received: { label: "Încasat", cls: "text-neon border-neon/50" },
  cancelled: { label: "Anulat", cls: "text-flame border-flame/50" },
};

const SponsorshipRow = ({ item, token, onChanged }) => {
  const [status, setStatus] = useState(item.status || "pending");
  const [amount, setAmount] = useState(item.received_amount ?? "");
  const [date, setDate] = useState(item.received_date || "");
  const [note, setNote] = useState(item.note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/form230/admin/sponsorships/${item.id}`,
        {
          status,
          received_amount: status === "received" && amount !== "" ? Number(amount) : null,
          received_date: status === "received" ? date || null : null,
          note,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sponsorizare actualizată.");
      onChanged();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Ștergi sponsorizarea de la ${item.company_name}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/form230/admin/sponsorships/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Sponsorizare ștearsă.");
      onChanged();
    } catch (err) {
      toast.error("Ștergerea a eșuat.");
    } finally {
      setBusy(false);
    }
  };

  const badge = SPONSOR_STATUS[item.status] || SPONSOR_STATUS.pending;

  return (
    <div
      data-testid="admin-sponsorship-row"
      className="bg-surface px-6 py-5 transition-colors duration-300 hover:bg-carddark"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
          {item.company_name}
          <span className="ml-3 font-mono text-[11px] font-normal normal-case tracking-normal text-neon">
            {item.amount} lei
          </span>
        </p>
        <div className="flex items-center gap-3">
          <span
            data-testid="admin-sponsorship-status-badge"
            className={`rounded-full border px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] ${badge.cls}`}
          >
            {badge.label}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {fmtDate(item.created_at)}
          </span>
        </div>
      </div>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
        CUI {item.cui} · {item.project}
      </p>
      <p className="mt-2 text-xs text-neutral-400">
        {item.representative} ({item.representative_role}) · {item.email || "fără email"}{" "}
        {item.phone ? `· ${item.phone}` : ""}
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-4">
        <div>
          <label className="mb-1.5 block font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Stare
          </label>
          <select
            data-testid="admin-sponsorship-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={`${inputCls} py-2.5 text-xs`}
          >
            <option value="pending">În așteptare</option>
            <option value="received">Încasat</option>
            <option value="cancelled">Anulat</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Sumă încasată
          </label>
          <input
            data-testid="admin-sponsorship-amount"
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={status !== "received"}
            className={`${inputCls} py-2.5 text-xs disabled:opacity-40`}
          />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Data încasării
          </label>
          <input
            data-testid="admin-sponsorship-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={status !== "received"}
            className={`${inputCls} py-2.5 text-xs disabled:opacity-40`}
          />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Notă
          </label>
          <input
            data-testid="admin-sponsorship-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex: contract semnat, OP trimis"
            className={`${inputCls} py-2.5 text-xs`}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          data-testid="admin-sponsorship-save"
          onClick={save}
          disabled={busy}
          className="flex items-center gap-2 rounded-xl bg-neon px-5 py-2.5 font-display text-[10px] font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white disabled:opacity-60"
        >
          Salvează
        </button>
        <button
          data-testid="admin-sponsorship-delete"
          onClick={remove}
          disabled={busy}
          className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400 transition-colors hover:border-flame hover:text-flame disabled:opacity-60"
        >
          <Trash2 className="h-3.5 w-3.5" /> Șterge
        </button>
      </div>
    </div>
  );
};

const Form230Row = ({ item, token, onDeleted }) => {
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Ștergi formularul 230 al lui ${item.full_name}? Fișierele semnate nu mai pot fi recuperate.`)) {
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API}/form230/admin/submissions/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Formular șters.");
      onDeleted();
    } catch (err) {
      toast.error("Ștergerea a eșuat.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-form230-row"
      className="bg-surface px-6 py-5 transition-colors duration-300 hover:bg-carddark"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
          {item.full_name}
          <span className="ml-3 font-mono text-[11px] font-normal normal-case tracking-normal text-neon">
            {item.email}
          </span>
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          {fmtDate(item.created_at)}
        </span>
      </div>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
        {item.phone || "fără telefon"} {item.county ? `· ${item.county}` : ""} · {item.files?.length || 0} fișier(e)
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        {(item.files || []).map((f) => (
          <button
            key={f.id}
            data-testid={`admin-form230-file-${f.id}`}
            onClick={() =>
              downloadPdf(
                `${API}/form230/admin/files/${f.id}`,
                f.original_filename,
                token,
                "Fișier descărcat"
              )
            }
            className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
          >
            <FileDown className="h-3.5 w-3.5" /> {f.original_filename?.slice(0, 28) || "fișier"}
          </button>
        ))}
        <button
          data-testid="admin-form230-delete"
          onClick={remove}
          disabled={busy}
          className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400 transition-colors hover:border-flame hover:text-flame disabled:opacity-60"
        >
          <Trash2 className="h-3.5 w-3.5" /> Șterge
        </button>
      </div>
    </div>
  );
};

const Form230GoalCard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [goal, setGoal] = useState("");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/form230/stats`);
      setStats(data);
      setGoal(String(data.goal));
      setExtra(String(data.manual_extra));
    } catch (err) {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(
        `${API}/form230/admin/goal`,
        { goal: Number(goal), manual_extra: Number(extra) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
      toast.success("Contorul public a fost actualizat.");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="admin-form230-goal"
      onSubmit={save}
      className="mt-8 rounded-3xl border border-white/10 bg-surface p-6 sm:p-8"
    >
      <p className="flex items-center gap-3 font-display text-sm font-black uppercase tracking-wide text-white">
        <Target className="h-4 w-4 text-neon" /> Contorul public
      </p>
      <p className="mt-3 text-xs leading-relaxed text-neutral-400">
        Pe pagina „3,5%” se afișează {stats?.total ?? 0} / {stats?.goal ?? 100} formulare:
        {" "}{stats?.uploaded ?? 0} încărcate pe site + {stats?.manual_extra ?? 0} adăugate manual
        (cele primite pe hârtie).
      </p>
      <div className="mt-6 grid max-w-lg gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400">
            Obiectiv
          </label>
          <input
            data-testid="admin-form230-goal-input"
            type="number"
            min="1"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400">
            Adăugate manual
          </label>
          <input
            data-testid="admin-form230-extra-input"
            type="number"
            min="0"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>
      <button
        type="submit"
        data-testid="admin-form230-goal-save"
        disabled={busy}
        className="mt-6 flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Salvează contorul
      </button>
    </form>
  );
};

const RegistrationRow = ({ reg, token }) => {
  const [open, setOpen] = useState(false);
  const [scans, setScans] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/admin/cupa/registrations/${reg.id}/scans`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setScans(data.items))
      .catch(() => {});
  }, [open, reg.id, token]);

  return (
    <div data-testid="admin-registration-row" className="bg-surface px-6 py-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <button
          type="button"
          data-testid={`admin-registration-toggle-${reg.id}`}
          onClick={() => setOpen((v) => !v)}
          className="text-left font-display text-sm font-bold uppercase tracking-wide text-white transition-colors hover:text-neon"
        >
          {reg.team_name}
          <span className="ml-3 font-mono text-[11px] font-normal normal-case tracking-normal text-neon">
            {reg.players?.length || 0} jucători
          </span>
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {fmtDate(reg.created_at)}
          </span>
          <button
            data-testid={`admin-pack-pdf-${reg.id}`}
            onClick={() =>
              downloadPdf(
                `${API}/admin/cupa/registrations/${reg.id}/pack`,
                `pachet-${reg.team_name}.pdf`,
                token
              )
            }
            className="flex items-center gap-1.5 rounded-full border border-neon/40 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neon transition-colors hover:bg-neon hover:text-canvas"
          >
            <FileDown className="h-3 w-3" /> Pachet toți jucătorii
          </button>
          <button
            data-testid={`admin-team-pdf-${reg.id}`}
            onClick={() =>
              downloadPdf(
                `${API}/admin/cupa/registrations/${reg.id}/pdf`,
                `echipa-${reg.team_name}.pdf`,
                token
              )
            }
            className="flex items-center gap-1.5 rounded-full bg-neon px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-canvas transition-colors hover:bg-white"
          >
            <FileDown className="h-3 w-3" /> PDF echipă
          </button>
        </div>
      </div>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        Căpitan: {reg.captain_name} · {reg.captain_phone} · {reg.captain_email}
      </p>
      {reg.notes && <p className="mt-2 text-sm text-neutral-400">{reg.notes}</p>}

      {open && (
        <div data-testid={`admin-registration-scans-${reg.id}`} className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon">
            Formulare scanate ({scans.length})
          </p>
          {scans.length === 0 ? (
            <p className="mt-2 text-xs text-neutral-500">
              Echipa nu a încărcat încă formulare semnate.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {scans.map((f) => (
                <li
                  key={f.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-carddark px-4 py-3"
                >
                  <span className="text-xs text-neutral-300">{f.original_filename}</span>
                  <button
                    data-testid={`admin-scan-open-${f.id}`}
                    onClick={() =>
                      downloadPdf(`${API}/admin/cupa/scans/${f.id}`, f.original_filename, token)
                    }
                    className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
                  >
                    <FileDown className="h-3 w-3" /> Descarcă
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open && (
        <div data-testid={`admin-registration-players-${reg.id}`} className="mt-5 space-y-2">
          {(reg.players || []).map((p, i) => (
            <div
              key={`${reg.id}-${i}`}
              className="rounded-xl border border-white/10 bg-carddark p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="text-sm font-bold text-white">
                  {i + 1}. {p.full_name}
                  <span className="ml-3 font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-neon">
                    {p.age} ani{p.is_minor ? " · minor" : ""}
                  </span>
                </p>
                <button
                  data-testid={`admin-player-pdf-${reg.id}-${i}`}
                  onClick={() =>
                    downloadPdf(
                      `${API}/admin/cupa/registrations/${reg.id}/players/${i}/pdf`,
                      `jucator-${p.full_name}.pdf`,
                      token
                    )
                  }
                  className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
                >
                  <FileDown className="h-3 w-3" /> PDF jucător
                </button>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-neutral-400">
                Născut: {p.birth_date} · {p.locality} · Tel: {p.phone || "-"} · Email: {p.email || "-"}
                <br />
                CI: {p.id_series || "-"} · CNP: {p.cnp || "-"}
                {p.is_minor && (
                  <>
                    <br />
                    Părinte: {p.guardian_name} ({p.guardian_relation || "-"}) · {p.guardian_phone} · CNP:{" "}
                    {p.guardian_cnp || "-"}
                  </>
                )}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StaffCodeCard = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/staff-code/status`, auth);
      setPending(data);
    } catch (err) {
      setPending(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const request = async (e) => {
    e.preventDefault();
    if (newCode.trim().length < 6) {
      toast.error("Codul nou trebuie să aibă minim 6 caractere.");
      return;
    }
    if (newCode.trim() !== confirmCode.trim()) {
      toast.error("Cele două coduri nu coincid.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/staff-code/request`,
        { new_code: newCode.trim(), confirm_code: confirmCode.trim() },
        auth
      );
      setPending(data);
      setNewCode("");
      setConfirmCode("");
      toast.success("Ți-am trimis un email de confirmare.");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/staff-code/resend`, {}, auth);
      setPending(data);
      toast.success("Email de confirmare retrimis.");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/staff-code/request`, auth);
      setPending({ pending: false });
      toast.success("Cererea a fost anulată. Codul actual rămâne valabil.");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-staff-code-card"
      className="mt-10 rounded-3xl border border-white/10 bg-surface p-6 sm:p-8"
    >
      <button
        type="button"
        data-testid="admin-staff-code-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <span className="flex items-center gap-3 font-display text-sm font-black uppercase tracking-wide text-white">
          <KeyRound className="h-4 w-4 text-neon" /> Schimbă codul de acces
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          {open ? "Închide" : "Deschide"}
        </span>
      </button>

      {open && (
        <div className="mt-6">
          {pending?.pending ? (
            <div data-testid="admin-staff-code-pending" className="rounded-2xl border-l-2 border-neon bg-carddark p-5">
              <p className="font-display text-xs font-black uppercase tracking-[0.18em] text-neon">
                În așteptarea confirmării pe email
              </p>
              <p className="mt-3 text-xs leading-relaxed text-neutral-300">
                Am trimis linkul de confirmare la {pending.notify_email}. Mai ai{" "}
                {pending.minutes_left} min. Până la confirmare, codul actual rămâne
                valabil.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="admin-staff-code-resend"
                  onClick={resend}
                  disabled={busy}
                  className="rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-neon hover:text-neon disabled:opacity-60"
                >
                  Retrimite emailul
                </button>
                <button
                  type="button"
                  data-testid="admin-staff-code-cancel"
                  onClick={cancel}
                  disabled={busy}
                  className="rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-flame hover:text-flame disabled:opacity-60"
                >
                  Anulează cererea
                </button>
              </div>
            </div>
          ) : (
            <form data-testid="admin-staff-code-form" onSubmit={request} className="max-w-md">
              <p className="text-xs leading-relaxed text-neutral-400">
                Scrie codul nou de două ori. Îl activăm doar după ce confirmi
                schimbarea din emailul trimis pe adresa asociației. Minim 6
                caractere.
              </p>
              <div className="mt-5">
                <label className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  Cod nou
                </label>
                <input
                  data-testid="admin-staff-code-new"
                  type="password"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="mt-4">
                <label className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  Repetă codul nou
                </label>
                <input
                  data-testid="admin-staff-code-confirm"
                  type="password"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  className={inputCls}
                />
              </div>
              <button
                type="submit"
                data-testid="admin-staff-code-submit"
                disabled={busy}
                className="mt-6 flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Trimite confirmarea pe email
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("messages");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [regOpen, setRegOpen] = useState(null);
  const [goalKey, setGoalKey] = useState(0);

  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (tab !== "cupa/registrations") return;
    axios
      .get(`${API}/admin/cupa/registration-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setRegOpen(data.is_open))
      .catch(() => setRegOpen(null));
  }, [tab, token]);

  const toggleRegistrations = async () => {
    try {
      const { data } = await axios.put(
        `${API}/admin/cupa/registration-status`,
        { is_open: !regOpen },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegOpen(data.is_open);
      toast.success(data.is_open ? "Înscrierile sunt deschise" : "Înscrierile sunt închise");
    } catch (err) {
      toast.error("Nu am putut schimba starea înscrierilor.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        tab === "form230"
          ? `${API}/form230/admin/submissions`
          : tab === "sponsorships"
          ? `${API}/form230/admin/sponsorships`
          : `${API}/admin/${tab}`,
        {
          params: !["form230", "sponsorships"].includes(tab) && query.trim() ? { q: query.trim() } : {},
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(res);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        toast.error("Sesiunea a expirat. Autentifică-te din nou.");
      } else {
        toast.error(errText(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [tab, query, token, onLogout]);

  useEffect(() => {
    const t = setTimeout(load, query ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, query]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lista-rise27.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descărcat");
    } catch (err) {
      toast.error("Exportul a eșuat. Încearcă din nou.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-20">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Overline>Panou Staff</Overline>
          <h1 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            Mesaje &amp; Înscrieri
          </h1>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
            Autentificat ca {user.email}
          </p>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={onLogout}
          className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-300 hover:border-flame hover:text-flame"
        >
          <LogOut className="h-3.5 w-3.5" /> Ieși
        </button>
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-3">
        {TABS.map(({ icon: Icon, ...t }) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => {
              setTab(t.id);
              setQuery("");
            }}
            className={`flex items-center gap-2 rounded-full px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === t.id
                ? "bg-neon text-canvas"
                : "border border-white/15 text-neutral-400 hover:border-neon hover:text-neon"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {tab !== "form230" && tab !== "sponsorships" && (
          <div className="relative min-w-[260px] flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                tab === "messages" ? "Caută după nume, email, subiect..." : "Caută după nume sau email..."
              }
              className={`${inputCls} pl-11`}
            />
          </div>
        )}
        <button
          data-testid="admin-refresh-button"
          onClick={load}
          className="flex items-center gap-2 rounded-xl border border-white/10 px-5 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-neon hover:text-neon"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
        </button>
        {tab === "waitlist" && (
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCsv}
            className="flex items-center gap-2 rounded-xl bg-neon px-5 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        )}
        {tab === "cupa/registrations" && regOpen !== null && (
          <button
            data-testid="admin-toggle-registrations"
            onClick={toggleRegistrations}
            className={`flex items-center gap-2 rounded-xl px-5 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              regOpen ? "bg-neon text-canvas hover:bg-white" : "border border-flame text-flame hover:bg-flame/10"
            }`}
          >
            {regOpen ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
            {regOpen ? "Înscrieri deschise" : "Înscrieri închise"}
          </button>
        )}
      </div>

      {tab === "cupa/registrations" && (
        <p
          data-testid="admin-registrations-status-note"
          className="mt-5 max-w-2xl text-xs leading-relaxed text-neutral-500"
        >
          Când înscrierile sunt deschise, formularul public cu semnătură
          electronică devine disponibil la /cupa-caritabila/inscriere. Închide-le
          oricând — pagina va afișa un mesaj că înscrierile nu sunt deschise.
        </p>
      )}

      {tab === "form230" && <Form230GoalCard key={goalKey} token={token} />}

      {tab === "sponsorships" && (
        <div data-testid="admin-sponsorship-totals" className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-3xl border border-neon/40 bg-neon/[0.06] p-6">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neon">
              Total încasat
            </p>
            <p data-testid="admin-total-received" className="mt-2 font-display text-3xl font-black text-white">
              {(data.total_received ?? 0).toLocaleString("ro-RO")} lei
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-surface p-6">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
              În așteptare (contracte generate)
            </p>
            <p data-testid="admin-total-pending" className="mt-2 font-display text-3xl font-black text-neutral-300">
              {(data.total_pending ?? 0).toLocaleString("ro-RO")} lei
            </p>
          </div>
        </div>
      )}

      <p data-testid="admin-result-count" className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-neon">
        {data.count}{" "}
        {tab === "messages"
          ? "mesaje"
          : tab === "cupa/registrations"
          ? "echipe înscrise"
          : tab === "form230"
          ? "formulare 230 primite"
          : tab === "sponsorships"
          ? "sponsorizări"
          : "înscrieri"}
      </p>

      {loading ? (
        <div className="mt-10 flex items-center gap-3 text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Se încarcă...
        </div>
      ) : data.items.length === 0 ? (
        <p data-testid="admin-empty-state" className="mt-10 text-sm text-neutral-500">
          Nimic de afișat deocamdată.
        </p>
      ) : tab === "sponsorships" ? (
        <div className="mt-8 space-y-px overflow-hidden rounded-2xl bg-white/10">
          {data.items.map((item) => (
            <SponsorshipRow key={item.id} item={item} token={token} onChanged={load} />
          ))}
        </div>
      ) : tab === "form230" ? (
        <div className="mt-8 space-y-px overflow-hidden rounded-2xl bg-white/10">
          {data.items.map((item) => (
            <Form230Row
              key={item.id}
              item={item}
              token={token}
              onDeleted={() => {
                setGoalKey((k) => k + 1);
                load();
              }}
            />
          ))}
        </div>
      ) : tab === "cupa/registrations" ? (
        <div className="mt-8 space-y-px overflow-hidden rounded-2xl bg-white/10">
          {data.items.map((reg) => (
            <RegistrationRow key={reg.id} reg={reg} token={token} />
          ))}
        </div>
      ) : (
        <div className="mt-8 space-y-px overflow-hidden rounded-2xl bg-white/10">
          {data.items.map((item) => (
            <div
              key={item.id}
              data-testid={
                tab === "messages"
                  ? "admin-message-row"
                  : tab === "cupa"
                  ? "admin-cupa-row"
                  : "admin-waitlist-row"
              }
              className="bg-surface px-6 py-5 transition-colors duration-300 hover:bg-carddark"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
                  {item.name || item.team_name || "Fără nume"}
                  <span className="ml-3 font-mono text-[11px] font-normal normal-case tracking-normal text-neon">
                    {item.email}
                  </span>
                </p>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  {fmtDate(item.created_at)}
                </span>
              </div>
              {tab === "messages" && (
                <>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
                    {item.subject}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-300">{item.message}</p>
                </>
              )}
              {tab === "cupa" && (
                <>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
                    {item.type} {item.team_name ? `· ${item.team_name}` : ""} {item.phone ? `· ${item.phone}` : ""}
                  </p>
                  {item.message && (
                    <p className="mt-3 text-sm leading-relaxed text-neutral-300">{item.message}</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <StaffCodeCard token={token} />
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-[70svh] items-center justify-center text-neutral-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginCard onLogin={setUser} />;
}
